import { useCallback, useEffect, useRef } from "react";
import "@/App.css";
import Lenis from "lenis";
import { motion, useScroll, useTransform } from "framer-motion";
import { Toaster } from "sonner";
import Navigation from "@/components/Navigation";
import HeroSection from "@/components/HeroSection";
import DepthHUD from "@/components/DepthHUD";
import Bubbles from "@/components/Bubbles";
import DepthChapter from "@/components/DepthChapter";
import Marquee from "@/components/Marquee";
import VentFooter from "@/components/VentFooter";
import ProjectsChapter from "@/components/ProjectsChapter";
import AboutSection from "@/components/AboutSection";

const IMAGES = {
  surface:
    "https://images.unsplash.com/photo-1484291470158-b8f8d608850d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxvY2VhbiUyMHN1cmZhY2UlMjB3YXZlcyUyMGFlcmlhbHxlbnwwfHx8fDE3ODczMDY1ODh8MA&ixlib=rb-4.1.0&q=85",
  coastal:
    "https://images.pexels.com/photos/32575747/pexels-photo-32575747.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  twilight:
    "https://images.unsplash.com/photo-1668110648714-1070e851f855?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxkZWVwJTIwc2VhJTIwdW5kZXJ3YXRlciUyMGRhcmt8ZW58MHx8fHwxNzg3MzA2NTg4fDA&ixlib=rb-4.1.0&q=85",
  midnight:
    "https://images.pexels.com/photos/4821643/pexels-photo-4821643.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const CHAPTERS = [
  {
    id: "capabilities",
    index: "01",
    zone: "Sunlight Zone",
    range: "0m — 200m",
    title: "Coastal & Environmental Modelling",
    fact: "Nearly 90% of all marine life lives in the sunlit zone — and every sound coastal decision begins with measuring it.",
    copy: "Current circulation, wave transformation, sediment transport, shoreline change, plume dispersion and oil-spill studies — our core discipline since inception — turn scattered measurements into foresight.",
    capabilities: [
      "Current Circulation",
      "Wave Transformation",
      "Harbour Wave Attenuation",
      "Sediment Transport",
      "Shoreline Change & Stability",
      "Coastal Morphodynamics",
      "Marina Flushing",
      "Plume Dispersion",
      "Oil Spill & Particle Tracking",
      "Outfall Optimisation",
    ],
    image: IMAGES.twilight,
    imageAlt: "Faint light rays filtering into the dark twilight zone of the ocean",
  },
  {
    id: "twilight",
    index: "02",
    zone: "Twilight Zone",
    range: "200m — 1000m",
    title: "Field Surveys & Investigations",
    fact: "Below 200m, sunlight thins to a faint blue veil. Where eyes fail, numerical models keep seeing.",
    copy: "Hydrographic, bathymetric, seismic and side-scan surveys; wave, tide and current measurements; water and sediment sampling — the raw truth every model is built on.",
    capabilities: [
      "Bathymetric Survey",
      "Upland Topographic",
      "Beach Profiling",
      "Wave · Tide · Current Measurement",
      "Water & Sediment Sampling",
      "Underwater Photo & Video",
    ],
    image: "/images/adcp-deployment.jpg",
    imageAlt: "ADCP acoustic doppler current profiler being deployed from a research vessel",
    flip: true,
  },
  {
    id: "midnight",
    index: "03",
    zone: "Midnight Zone",
    range: "1000m — 3000m",
    title: "Environmental Assessment & Metocean",
    fact: "No sunlight ever reaches the midnight zone. The pressure here exceeds 300 atmospheres.",
    copy: "Baseline surveys, compliance monitoring, fisheries and mangrove restoration, storm-surge and inundation assessment — strategic assistance from first sample to final approval.",
    capabilities: [
      "Baseline Surveys",
      "Compliance Monitoring",
      "Physical · Chemical · Biological Analysis",
      "Sediment Quality",
      "Fisheries",
      "Mangrove Restoration",
      "Risk Assessment & Due Diligence",
      "Extreme Design Parameters",
      "Storm Surge & Inundation",
      "Wave & Current Hindcast / Forecast",
    ],
    image: IMAGES.midnight,
    imageAlt: "Divers inside a deep underwater cave in the midnight zone",
  },
  {
    id: "seafloor",
    index: "04",
    zone: "The Abyssal Plain",
    range: "≈ 3000m",
    title: "Structures, Moorings & Geo-Spatial",
    fact: "Hydrothermal vents can exceed 400°C — yet entire ecosystems thrive here without a single ray of sunlight.",
    copy: "Mooring system design, vessel manoeuvring and navigation studies, plus GIS mapping from survey planning to reporting — engineering that holds its ground at the very bottom of the sea.",
    capabilities: [
      "Coastal & Marine Structures Design",
      "Mooring System Design",
      "Vessel Motion & Manoeuvring",
      "Navigation Studies",
      "Land Use Mapping",
      "Coastal Geomorphology Mapping",
      "Coastal Inventory",
      "Habitat Mapping",
      "Shoreline Change Assessment",
    ],
    image: IMAGES.coastal,
    imageAlt: "Concrete coastal breakwater structures at the shoreline",
  },
  {
    id: "cfd",
    index: "05",
    zone: "The Abyss",
    range: "3000m+",
    title: "Advanced CFD Modelling",
    fact: "The deepest ocean trench would swallow Mount Everest with more than 2km to spare.",
    copy: "High-resolution 2D and 3D CFD for wave–structure interaction and free-surface flows — resolving the physics that decide whether a structure stands or fails.",
    capabilities: [
      "High-Resolution 2D/3D CFD",
      "Wave–Structure Interaction",
      "Free-Surface Flows",
      "Turbulence & Vortex Dynamics",
      "Local Scour",
      "Overtopping",
      "Breakwaters, Seawalls & Marine Structures",
      "Outfalls & Intake Hydraulics",
    ],
    image: "https://images.pexels.com/photos/3862139/pexels-photo-3862139.jpeg?auto=compress&cs=tinysrgb&w=940",
    imageAlt: "Water flowing over a weir — fluid dynamics resolved in high-resolution CFD",
  },
  {
    id: "ai-modelling",
    index: "06",
    zone: "The Abyss",
    range: "3000m+",
    title: "AI-Enabled Modelling & Data Analytics",
    fact: "More people have walked on the Moon than have visited the deepest point of the ocean.",
    copy: "Machine-learning-assisted modelling, surrogate models and automated calibration — faster answers from the same physics, with uncertainty made explicit.",
    capabilities: [
      "Machine-Learning-Assisted Modelling",
      "Surrogate & Reduced-Order Models",
      "Automated Calibration & Optimisation",
      "AI-Based Forecasting",
      "Model Emulation",
      "Uncertainty Analysis",
      "Physics-Informed Machine Learning",
      "Automated Metocean Dataset Interpretation",
    ],
    image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=940&q=80",
    imageAlt: "Abstract visualisation of a neural network — AI-assisted numerical modelling",
    flip: true,
  },
  {
    id: "digital-twins",
    index: "07",
    zone: "The Abyss",
    range: "3000m+",
    title: "Digital Twins & Decision Support",
    fact: "We have better maps of Mars than of our own seafloor — barely a quarter has been mapped in high resolution.",
    copy: "Coastal and marine digital twins with real-time metocean assimilation — operational forecasts, early warning and scenario simulation in one decision platform.",
    capabilities: [
      "Coastal & Marine Digital Twins",
      "Real-Time Metocean Assimilation",
      "Operational Wave / Current Forecasts",
      "Flood & Inundation Early Warning",
      "Asset-Performance Monitoring",
      "Scenario Simulation",
      "Predictive Maintenance",
      "Interactive Decision-Support Platforms",
    ],
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=940&q=80",
    imageAlt: "Real-time analytics dashboard on screen — digital twin decision support",
  },
];

function App() {
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.5,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    lenisRef.current = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  const scrollTo = useCallback((target) => {
    lenisRef.current?.scrollTo(target, { duration: 2.4 });
  }, []);

  const { scrollYProgress } = useScroll();
  const backgroundColor = useTransform(
    scrollYProgress,
    [0, 0.05, 0.15, 0.333, 1],
    ["#C8F2F4", "#6FD0D6", "#0A93A6", "#05445E", "#020B14"]
  );

  return (
    <div className="App relative" data-testid="okeanos-app">
      <motion.div
        className="fixed inset-0 -z-20"
        style={{ backgroundColor }}
        data-testid="depth-background"
        aria-hidden="true"
      />
      <div className="noise-overlay" aria-hidden="true" />
      <Navigation scrollYProgress={scrollYProgress} onNavigate={scrollTo} />
      <DepthHUD scrollYProgress={scrollYProgress} />
      <Bubbles />
      <main className="relative z-[2]">
        <HeroSection
          surfaceImage={IMAGES.surface}
          onBegin={() => scrollTo("#capabilities")}
        />
        <AboutSection />
        <DepthChapter {...CHAPTERS[0]} />
        <DepthChapter {...CHAPTERS[1]} />
        <Marquee />
        <DepthChapter {...CHAPTERS[2]} />
        <DepthChapter {...CHAPTERS[3]} />
        <DepthChapter {...CHAPTERS[4]} />
        <DepthChapter {...CHAPTERS[5]} />
        <DepthChapter {...CHAPTERS[6]} />
        <ProjectsChapter />
        <VentFooter onNavigate={scrollTo} />
      </main>
      <Toaster
        theme="dark"
        position="bottom-center"
        toastOptions={{
          style: {
            background: "#020B14",
            border: "1px solid rgba(245, 130, 32, 0.35)",
            color: "#F8FAFC",
            fontFamily: "'IBM Plex Mono', monospace",
          },
        }}
      />
    </div>
  );
}

export default App;
